import { parseFrontmatter } from "./frontmatter";
import { DROPPED_KEYS, EDITABLE_KEYS } from "./entryModel";

const LIST_KEYS = new Set(["tags", "links"]);

function typedValue(key, value) {
	if (LIST_KEYS.has(key)) {
		try {
			return JSON.parse(value);
		} catch {
			return value
				.replace(/[[\]"]/g, "")
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean);
		}
	}
	if (value === "true" || value === "false") return value === "true";
	return value;
}

/**
 * Parse raw markdown into `{ slug, metadata, body, raw }` with typed
 * frontmatter: `tags`/`links` as arrays, "true"/"false" as booleans. A file
 * without frontmatter keeps its whole text as the body.
 * @param {string} slug file slug; a `slug:` in the frontmatter overrides it in metadata
 * @param {string} content
 */
export function parseEntry(slug, content) {
	const { meta, body, hasFrontmatter } = parseFrontmatter(content);
	if (!hasFrontmatter) return { slug, metadata: {}, body: content, raw: content };
	const metadata = { slug };
	for (const [key, value] of Object.entries(meta)) metadata[key] = typedValue(key, value);
	return { slug, metadata, body, raw: content };
}

function frontmatterLine(key, value) {
	if (Array.isArray(value)) return `${key}: ${JSON.stringify(value)}`;
	if (typeof value === "string" && (value.includes(":") || value.includes("#"))) {
		return `${key}: "${value}"`;
	}
	return `${key}: ${value}`;
}

const isWritable = (value) => value !== undefined && value !== "";

/**
 * Markdown with frontmatter from metadata and body: EDITABLE_KEYS first in
 * their order, then any unknown keys; DROPPED_KEYS and empty values are not
 * written.
 * @param {Record<string, any>} metadata
 * @param {string} [body]
 */
export function serializeEntry(metadata, body) {
	const keys = [
		...EDITABLE_KEYS,
		...Object.keys(metadata).filter(
			(k) => !EDITABLE_KEYS.includes(k) && !DROPPED_KEYS.includes(k),
		),
	];
	const lines = keys
		.filter((k) => !DROPPED_KEYS.includes(k) && isWritable(metadata[k]))
		.map((k) => frontmatterLine(k, metadata[k]));
	return ["---", ...lines, "---", "", body || ""].join("\n");
}
